import { z } from 'zod';
import { cleanString } from './clean.js';
import { EngagementStatus } from './onboarding-enums.js';
import { VendorDocument } from './vendor.js';

// ── Detail (T-090) ───────────────────────────────────────────────────
// The engagement page = one work arrangement (resource × client × rates),
// which is the QuickBooks Service item. Links out to its resource, vendor,
// and client. rate_to_client is OMITTED (not nulled) for every non-owner
// (T-186) — one schema parses both shapes, mirroring VendorEngagement.
export const EngagementDetail = z.object({
  id: z.string().uuid(),
  product_service: z.string(),
  status: EngagementStatus,
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  rate_to_contractor: z.number().nullable(),
  rate_to_client: z.number().nullable().optional(),
  uit_contact_email: z.string().nullable(),
  client_id: z.string().uuid(),
  client_name: z.string(),
  resource_id: z.string().uuid(),
  resource_name: z.string(),
  vendor_id: z.string().uuid(),
  business_name: z.string(),
  // The referring vendor (T-109), if this engagement came from a referral —
  // distinct from the business vendor above. null = not a referral. The
  // owner-only referral_fee_per_hour is deferred to Wave 3, so it is not here.
  referred_by_vendor_id: z.string().uuid().nullable(),
  referred_by_vendor_name: z.string().nullable(),
  // The Cigna manager this placement reports to (T-131), chosen on the RCF
  // review screen (T-115). null until set; the manager drives the RCF expense
  // center + recipients. ON DELETE SET NULL → a deleted manager nulls this.
  cigna_manager_id: z.string().uuid().nullable(),
  // The executed subcontractor agreement (null until signing completes).
  signed_contract: VendorDocument.nullable(),
});
export type EngagementDetail = z.infer<typeof EngagementDetail>;

// ── Update (T-131, +product_service T-172) ───────────────────────────
// The editable subset of an engagement: the RCF's Cigna-manager link (set on
// T-115's review screen) and the product/service name (the QuickBooks Service
// item label, edited on the engagement detail page). PATCH semantics: an
// omitted key is left untouched; an explicit null clears the link.
//
// product_service is cleanString()-normalized (T-200) — this field is exactly
// where the prod incident entered, via a name pasted with invisible characters
// that then broke the billing output's display-name match.
//
// The vendor is deliberately NOT here: it lives on the resource, not the
// engagement, so changing it ripples to sibling engagements and needs its own
// confirmed, audited endpoint (EngagementVendorReassignInput below).
export const EngagementUpdateInput = z.object({
  cigna_manager_id: z.string().uuid().nullable().optional(),
  product_service: cleanString(z.string().min(1).max(400)).optional(),
});
export type EngagementUpdateInput = z.infer<typeof EngagementUpdateInput>;

// ── Vendor reassignment (T-172) ──────────────────────────────────────
// Move this engagement to a different vendor. The vendor is a single FK on the
// RESOURCE (migration 018), so this repoints resources.vendor_id and every one
// of that resource's engagements follows — which is why the UI confirms the
// consequences first and the server audits the change. The engagement row
// itself survives unchanged (no duplicate QuickBooks Service), and billing
// history is never rewritten: past runs keep their own frozen payload.
//
// Its own endpoint by design, outside EngagementUpdateInput, so an ordinary
// engagement PATCH can never move a vendor as a side effect.
export const EngagementVendorReassignInput = z.object({
  vendor_id: z.string().uuid(),
});
export type EngagementVendorReassignInput = z.infer<typeof EngagementVendorReassignInput>;

// ── Referral fee (T-170) ─────────────────────────────────────────────
// Owner-only, engagement-grain. Kept OUT of EngagementDetail (an admin must not
// see it) — fetched/set via the owner-gated /referral-fee endpoints. Reference
// only: commissions are computed off-portal (T-197), so nothing in the app
// derives from this value.
export const EngagementReferralFee = z.object({
  referral_fee_per_hour: z.number().nonnegative().nullable(),
});
export type EngagementReferralFee = z.infer<typeof EngagementReferralFee>;

// ── Rate to client (T-194) ───────────────────────────────────────────
// Owner-only write for the margin numerator. Before this, rate_to_client was
// settable ONLY at onboarding create, so an onboarding created by a non-owner
// landed null permanently — not even an owner could correct it afterwards
// (the hole T-186's write-path fix exposed).
//
// Its own endpoint for the same reason the vendor has one: PATCH
// /api/engagements/:id is admin-role gated, and this field must never be
// settable by a non-owner. The READ path is untouched — rate_to_client stays
// on EngagementDetail, omitted (not nulled) for non-owners per T-186.
//
// null clears the rate, mirroring EngagementReferralFee.
export const EngagementRateToClient = z.object({
  rate_to_client: z.number().nonnegative().nullable(),
});
export type EngagementRateToClient = z.infer<typeof EngagementRateToClient>;
